<template>
  <div>
    <label :for="id" class="form-label">
      {{ label }}
      <span v-if="validate.includes('required') && !disabled" class="required-status">*</span>
    </label>

    <div v-if="!disabled" class="svg-container">
      <div class="d-flex align-items-center gap-2">
        <BButton variant="primary" @click.prevent="reset" title="reset" :disabled="imagesList.length == 1">
          <i class="mdi mdi-lock-reset"></i>
        </BButton>
        <BButton variant="primary" @click.prevent="undo" title="undo" :disabled="imagesList.length == 1">
          <i class="mdi mdi-undo"></i>
        </BButton>

        <button
          v-for="c in colorList"
          :key="c"
          :class="`color-btn ${currentColor === c ? 'color-btn-active' : ''}`"
          :style="`--bg-color-btn: ${c}`"
          @click.prevent="changeColor(c)"
        ></button>

        <button :class="{ disabled: loading }" class="btn btn-success d-flex gap-2" @click.prevent="saveFile">
          <LoadingSpinner v-if="loading" />
          <i v-else class="mdi mdi-upload"></i>
          {{ $t('main.save') }}
        </button>
      </div>
      <div class="w-100" :id="`main-svg-editor-${id}`" @click="changePathColor">
        <div v-svg="newUrl" />
      </div>

      <div
        v-if="(Array.isArray(errors) && errors.length) || errors.length || meta?.errors?.length"
        class="invalid-feedback"
      >
        {{ Array.isArray(errors) ? errors[0] : errors }}
        {{ Array.isArray(meta.errors) ? meta.errors[0] : '' }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, defineProps, defineEmits, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { customAlert } from '@/utils/sweetalert';
import { getFileURL, svgToBlob } from '@/utils/helper';
import { commonUploadFile, generatePresignedUrl } from '@/services/main';

const { t } = useI18n();
const emit = defineEmits(['change']);

const loading = ref(false);

const props = defineProps({
  id: { type: String, default: '' },
  label: { type: String, default: '' },
  validate: { type: [String, Object], default: '' },
  errors: { type: [String, Array], default: '' },
  url: { type: String, default: '' },

  uploadStatus: { type: Boolean, default: false },
  uploadUrl: { type: String, default: '' },
  uploadFilePath: { type: String, default: '' },
});

const newUrl = ref(getFileURL(props.url) || '');
const imagesList = ref([]);

const currentColor = ref('#e5383b');
const colorList = ref(['#e5383b', '#9de6a3', '#4361ee']);

const changeColor = (color) => {
  currentColor.value = color;
};

const changePathColor = (event) => {
  if (event.srcElement.tagName === 'path') {
    event.srcElement.setAttribute('fill-opacity', 1);
    event.srcElement.setAttribute('fill', currentColor.value);
    event.srcElement.style.setProperty('fill', currentColor.value, 'important');

    const currentElement = document.getElementById(`main-svg-editor-${props.id}`).innerHTML;
    imagesList.value.push(currentElement);
    // uploadTheFile(svgToBlob(currentElement));
  }
};

const undo = () => {
  const currentElement = imagesList.value[imagesList.value.length - 2];
  document.getElementById(`main-svg-editor-${props.id}`).innerHTML = currentElement;
  // uploadTheFile(svgToBlob(currentElement));
  imagesList.value.pop();
};
const reset = () => {
  const currentElement = imagesList.value[0];
  document.getElementById(`main-svg-editor-${props.id}`).innerHTML = currentElement;
  // uploadTheFile(svgToBlob(currentElement));
  imagesList.value = [currentElement];
};

const saveFile = () => {
  const currentElement = document.getElementById(`main-svg-editor-${props.id}`).innerHTML;
  imagesList.value.push(currentElement);
  uploadTheFile(svgToBlob(currentElement));
};

const uploadTheFile = (file, showSuccessAlert = true) => {
  if (props.uploadStatus) {
    loading.value = true;
    const formData = new FormData();
    formData.append('filename', file?.name);
    formData.append('path', props.uploadFilePath);
    generatePresignedUrl(props.uploadUrl, formData)
      .then((res) => {
        const { uploading_url, headers } = res.data;
        commonUploadFile(uploading_url, file, headers)
          .then(() => {
            emit('change', res.data.file_url || '');
            if (showSuccessAlert) customAlert(t('main.done'), 'success');
          })
          .catch(() => {
            emit('change', '');
          })
          .finally(() => {
            loading.value = false;
          });
      })
      .catch(() => {
        emit('change', '');
        loading.value = false;
      });
  } else {
    emit('change', file);
  }
};

onMounted(() => {
  setTimeout(() => {
    const currentElement = document.getElementById(`main-svg-editor-${props.id}`).innerHTML;
    imagesList.value = [currentElement];
    uploadTheFile(svgToBlob(currentElement), false);
  }, 500);
});
</script>

<style lang="scss">
.svg-container {
  .color-btn {
    width: 72px;
    height: 40px;
    border-radius: 8px;
    cursor: pointer;
    border: 0px;
    background-color: var(--bg-color-btn);
    &.color-btn-active {
      border: 2px solid #000;
    }
  }
  svg {
    width: 100%;
    height: 100%;
    path {
      cursor: pointer;
    }
  }
}
</style>
